import type {
  AccuracyRange,
  AccuracyRolls,
  ImpulseAccuracyConfig,
  ResolvedAccuracy,
} from "../types/accuracy";
import type { Vector2D } from "../types/dynamics";
import type { RandomProvider } from "../types/random-provider";
import { magnitude, rotate, scale } from "./vector";

export function computeEffectiveAccuracy(
  range: AccuracyRange,
  roll: number,
): number {
  return range.min + (range.max - range.min) * roll;
}

export function sampleAccuracyRolls(provider: RandomProvider): AccuracyRolls {
  return {
    powerRoll: provider.next(),
    directionRoll: provider.next(),
  };
}

export function resolveAccuracy(
  config: ImpulseAccuracyConfig,
  rolls: AccuracyRolls,
): ResolvedAccuracy {
  const powerAccuracy = computeEffectiveAccuracy(config.power, rolls.powerRoll);
  const directionAccuracy = computeEffectiveAccuracy(
    config.direction,
    rolls.directionRoll,
  );
  const maxErrorRad = (config.directionMaxErrorDeg * Math.PI) / 180;
  const directionErrorRad = (1 - directionAccuracy) * maxErrorRad;

  return {
    powerAccuracy,
    directionAccuracy,
    directionErrorRad,
  };
}

export function resolveImpulseAccuracy(
  impulse: Vector2D,
  config: ImpulseAccuracyConfig,
  rolls: AccuracyRolls,
): Vector2D {
  const resolved = resolveAccuracy(config, rolls);
  const mag = magnitude(impulse);
  const adjustedMagnitude = mag * resolved.powerAccuracy;

  const directionSign = rolls.directionRoll >= 0.5 ? 1 : -1;
  const rotated = rotate(impulse, resolved.directionErrorRad * directionSign);

  const rotatedMag = magnitude(rotated);
  if (rotatedMag === 0) {
    return { x: 0, y: 0 };
  }

  return scale(rotated, adjustedMagnitude / rotatedMag);
}
